import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { baseAxios } from "../../../utils/config";
import { AppointmentDetail } from "./appointment-detail";

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

function parseJsonField(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
}

/**
 * Stylist View — client lookup and appointment formula history
 */
export function StylistView({
  salon,
  startDate,
  endDate,
  formatDateToISO,
  user,
  needsLocation,
}) {
  const [clientOptions, setClientOptions] = useState([]);
  const [clientInput, setClientInput] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchingClients, setSearchingClients] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [sortBy, setSortBy] = useState("appointment_date");
  const [sortOrder, setSortOrder] = useState("desc");

  // Debounced client search
  useEffect(() => {
    if (!clientInput || clientInput.trim().length < 2) {
      setClientOptions([]);
      return undefined;
    }
    if (needsLocation || !salon) return undefined;

    const timer = setTimeout(async () => {
      setSearchingClients(true);
      try {
        const response = await baseAxios.get("/formula-tracker/clients", {
          params: {
            search: clientInput.trim(),
            locationId: salon?.location_id || user.location_id,
          },
        });
        setClientOptions(Array.isArray(response.data) ? response.data : []);
      } catch {
        setClientOptions([]);
        toast.error("Failed to search clients", { duration: 3000 });
      } finally {
        setSearchingClients(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [clientInput, needsLocation, salon, user.location_id]);

  const loadAppointments = useCallback(async () => {
    if (needsLocation || !salon) {
      toast.error("Please select a location first", { duration: 3000 });
      return;
    }
    if (!selectedClient?.client_id) {
      toast.error("Please select a client", { duration: 3000 });
      return;
    }

    setLoading(true);
    setHasRun(true);
    try {
      const params = {
        clientId: selectedClient.client_id,
        locationId: salon?.location_id || user.location_id,
      };
      if (startDate) params.startDate = formatDateToISO(startDate);
      if (endDate) params.endDate = formatDateToISO(endDate);

      const response = await baseAxios.get("/formula-tracker/appointments", {
        params,
      });

      if (!response.data) {
        toast.error("No data returned from server", { duration: 3000 });
        setRows([]);
        return;
      }

      const transformed = (Array.isArray(response.data) ? response.data : []).map(
        (item, index) => ({
          ...item,
          uniqueKey: `${item.schedule_entry_id}-${index}`,
          services: parseJsonField(item.services),
          products: parseJsonField(item.products),
          servicesLabel: parseJsonField(item.services)
            .map((s) => s.service_name)
            .filter(Boolean)
            .join(", "),
          productsCount: parseJsonField(item.products).length,
        })
      );
      setRows(transformed);
    } catch (error) {
      setRows([]);
      if (error.response?.status === 403) {
        toast.error("Access denied", { duration: 5000 });
      } else if (error.response?.status === 404) {
        toast.error("No appointments found for this client", { duration: 3000 });
      } else {
        toast.error(
          error.response?.data?.message || "Failed to load appointments",
          { duration: 5000 }
        );
      }
    } finally {
      setLoading(false);
    }
  }, [
    endDate,
    formatDateToISO,
    needsLocation,
    salon,
    selectedClient,
    startDate,
    user.location_id,
  ]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const columns = useMemo(
    () => [
      {
        id: "appointment_date",
        label: "Appointment Date",
        field: "appointment_date",
        minWidth: 180,
        render: (value) =>
          value && dayjs(value).isValid()
            ? dayjs(value).format("M/D/YY h:mm A")
            : "—",
      },
      {
        id: "stylist_name",
        label: "Stylist",
        field: "stylist_name",
        minWidth: 150,
        render: (v) => v || "—",
      },
      {
        id: "servicesLabel",
        label: "Services Performed",
        field: "servicesLabel",
        minWidth: 220,
        render: (v) => v || "—",
      },
      {
        id: "productsCount",
        label: "Products Used",
        field: "productsCount",
        minWidth: 120,
        align: "center",
        render: (v) => Number(v) || 0,
      },
    ],
    []
  );

  const renderDetail = useCallback(
    (row) => <AppointmentDetail data={row} />,
    []
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          mb: 2.5,
        }}
      >
        <Autocomplete
          sx={{ minWidth: 280, flex: 1, maxWidth: 480 }}
          options={clientOptions}
          value={selectedClient}
          inputValue={clientInput}
          onInputChange={(_e, value) => setClientInput(value)}
          onChange={(_e, value) => {
            setSelectedClient(value);
            setRows([]);
            setHasRun(false);
          }}
          getOptionLabel={(option) => option?.client_name || ""}
          isOptionEqualToValue={(a, b) => a?.client_id === b?.client_id}
          loading={searchingClients}
          disabled={needsLocation}
          filterOptions={(x) => x}
          noOptionsText={
            clientInput.trim().length < 2
              ? "Type at least 2 characters"
              : "No clients found"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search client"
              size="small"
              placeholder="Client name (min 2 characters)"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "background.paper",
                  borderRadius: "6px",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchingClients ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={loadAppointments}
          disabled={loading || needsLocation || !selectedClient}
          sx={{
            minHeight: 40,
            px: 2,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {loading ? "Loading…" : "Load Report"}
        </Button>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedRows}
        totalRowCount={sortedRows.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="appointment_date"
        initialOrder="desc"
        searchQuery={`${selectedClient?.client_id || "none"}|${formatDateToISO(startDate)}|${formatDateToISO(endDate)}`}
        clientSidePagination
        skeletonHeight="40px"
        getRowKey={(row) => row.uniqueKey}
        renderDetail={renderDetail}
        emptyMessage={
          !selectedClient
            ? "Select a client to view appointment history"
            : hasRun
              ? "No appointments found for this client"
              : "Click Load Report to view appointment history"
        }
      />
    </Box>
  );
}
